export interface WeightAttr {
  id: number;
  name: string;
  slug: string;
  default_weight: number | string;
  /** Industry-lens weight — a second vector through the same kernel. */
  industry_weight?: number | string;
  order: number;
}

export type WeightMap = Record<number, number>;

/** The two weight profiles the kernel can run with. */
export type WeightProfile = 'match' | 'industry';

// Normalize raw stored weights into proportions that sum to 1. The backend
// re-normalizes at score time anyway, so the UI presents a clean budget. If
// every weight is zero (or there are none), fall back to an equal split.
// `key` picks the profile column: 'default_weight' (Match) or
// 'industry_weight' (Industry lens).
export function normalizeWeights(
  attrs: WeightAttr[],
  key: 'default_weight' | 'industry_weight' = 'default_weight',
): WeightMap {
  const out: WeightMap = {};
  if (attrs.length === 0) return out;
  const total = attrs.reduce((s, a) => s + (Number(a[key]) || 0), 0);
  if (total <= 0) {
    const eq = 1 / attrs.length;
    attrs.forEach((a) => {
      out[a.id] = eq;
    });
    return out;
  }
  attrs.forEach((a) => {
    out[a.id] = (Number(a[key]) || 0) / total;
  });
  return out;
}

// Set attribute `id` to `value` (clamped to [0, 1]) and rebalance every OTHER
// attribute so the whole set still sums to 1. The remaining budget (1 − value)
// is distributed across the others proportionally to their current weights, so
// their relative ratios are preserved. If the others are all zero, the budget
// is split equally among them.
export function rebalance(weights: WeightMap, id: number, value: number): WeightMap {
  const ids = Object.keys(weights).map(Number);
  const v = Math.min(1, Math.max(0, value));
  const others = ids.filter((k) => k !== id);
  const next: WeightMap = { ...weights, [id]: v };
  if (others.length === 0) {
    next[id] = 1;
    return next;
  }
  const remaining = 1 - v;
  const sumOthers = others.reduce((s, k) => s + (weights[k] || 0), 0);
  if (sumOthers > 1e-9) {
    others.forEach((k) => {
      next[k] = (weights[k] || 0) * (remaining / sumOthers);
    });
  } else {
    const eq = remaining / others.length;
    others.forEach((k) => {
      next[k] = eq;
    });
  }
  return next;
}
